import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  clearPurchaseFlowData,
  getPurchaseFlowData,
} from "utils/purchase-flow-storage";
import { useGlobalSlice } from "app/slice";

/**
 * Purchase Flow Navigation Guard
 * Clears purchase flow data from sessionStorage when navigating to routes that are not purchase flow routes
 */
const PURCHASE_FLOW_ROUTES = [
  "/network-plan",
  "/multi-network-selection",
  "/single-network-selection",
  "/multi-network-phone-type",
  "/single-network-phone-type",
  "/multi-network-number-type",
  "/single-network-number-type",
  "/checkout",
];

const PURCHASE_FLOW_EXIT_FLAG = "purchaseFlowExited";

const isPurchaseFlowRoute = (path: string): boolean => {
  return PURCHASE_FLOW_ROUTES.includes(path);
};

const clearPurchaseFlowState = (dispatch: any, actions: any) => {
  clearPurchaseFlowData();
  dispatch(actions.setPlanData({ planData: null }));
  dispatch(actions.setLines({ numberOfLines: null }));
  dispatch(actions.collectCheckAvailabilityDetails({ CAD: null }));
  dispatch(actions.setCompatibilityData({ CD: null }));
  dispatch(actions.setCompatibilitiesData({ CDS: [] }));
  dispatch(actions.setIsPorting({ isPorting: false }));
  dispatch(actions.setPortRequestSubmitted(false));
};

export const PurchaseFlowNavigationGuard = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { actions } = useGlobalSlice();
  const previousPathRef = useRef<string>(location.pathname);

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;
    const isOnPurchaseFlow = isPurchaseFlowRoute(currentPath);
    const wasOnPurchaseFlow = isPurchaseFlowRoute(previousPath);

    // If current route is not a purchase flow route, clear the data
    if (!isOnPurchaseFlow) {
      const hasPurchaseFlowData = getPurchaseFlowData() !== null;
      if (hasPurchaseFlowData) {
        clearPurchaseFlowState(dispatch, actions);
        // Set flag to indicate we've exited purchase flow
        sessionStorage.setItem(PURCHASE_FLOW_EXIT_FLAG, "true");
      }
    } else {
      // If we're entering a purchase flow route
      // Check if we previously exited purchase flow (via external navigation)
      const hasExitedFlag =
        sessionStorage.getItem(PURCHASE_FLOW_EXIT_FLAG) === "true";

      // Also check if we're coming from a non-purchase-flow route
      if (
        hasExitedFlag ||
        (!wasOnPurchaseFlow && previousPath !== currentPath)
      ) {
        // Clear stale purchase flow data
        const hasPurchaseFlowData = getPurchaseFlowData() !== null;
        if (hasPurchaseFlowData) {
          clearPurchaseFlowState(dispatch, actions);
        }
        // Remove the exit flag
        sessionStorage.removeItem(PURCHASE_FLOW_EXIT_FLAG);
      }
    }

    // Update previous path
    previousPathRef.current = currentPath;
  }, [location.pathname, dispatch, actions]);

  return null;
};
